import { useSyncExternalStore } from "react";

// Per-session "seen" state for the schedule pre-call brief's new-patient-activity.
// Baseline is stamped the first time a visit is seen this session; anything that arrives
// afterwards reads as "new" until the row is collapsed, which re-stamps the record.
// Choice B: sessionStorage (survives a reload in the same tab, resets on tab close).
const KEY = "revq.th.seen";
let store = (() => { try { return JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch { return {}; } })();
let ver = 0;
const subs = new Set();
const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(store)); } catch { /* full */ } ver++; subs.forEach((f) => f()); };

const hasDocs = (a) => Array.isArray(a.patient_documents);
const hasQs = (a) => Array.isArray(a.patient_questions);
const docIds = (a) => (hasDocs(a) ? a.patient_documents.map((x) => x.doc_id || x.id).filter(Boolean) : []);
const qKey = (x) => `q:${x.id || x.text || x.question || ""}`;
const qKeys = (a) => (hasQs(a) ? a.patient_questions.map(qKey) : []);
export const intakeCount = (a) => (a.intake_answered_count != null ? a.intake_answered_count : (a.intake_answers || []).length);

const snapshot = (a) => ({ docs: docIds(a), questions: qKeys(a), intake: intakeCount(a) });
const rec = (a) => (a ? store[a.appointment_id] : null);

// Baseline once per visit per session.
export function observe(rows) {
  let changed = false;
  rows.forEach((a) => { const id = a.appointment_id; if (id && !store[id]) { store[id] = snapshot(a); changed = true; } });
  if (changed) save();
}

// Re-stamp everything currently present as seen (called on every collapse route).
export function writeSeen(a) {
  if (!a?.appointment_id) return;
  store[a.appointment_id] = snapshot(a);
  save();
}

export const newDocIds = (a) => { const s = rec(a); if (!s || !hasDocs(a)) return []; const seen = new Set(s.docs); return docIds(a).filter((id) => !seen.has(id)); };
export const isDocNew = (a, docId) => { const s = rec(a); return !!s && hasDocs(a) && !s.docs.includes(docId); };
export const newDocCount = (a) => newDocIds(a).length;

export const newQuestionKeys = (a) => { const s = rec(a); if (!s || !hasQs(a)) return []; const seen = new Set(s.questions); return qKeys(a).filter((k) => !seen.has(k)); };
export const isQuestionNew = (a, x) => { const s = rec(a); return !!s && hasQs(a) && !s.questions.includes(qKey(x)); };
export const newQuestionCount = (a) => newQuestionKeys(a).length;

export const newAnswerCount = (a) => { const s = rec(a); return s ? Math.max(0, intakeCount(a) - s.intake) : 0; };
export const firstNewAnswerIndex = (a) => { const s = rec(a); return s && intakeCount(a) > s.intake ? s.intake : -1; };
export const isAnswerNew = (a, index) => { const s = rec(a); return !!s && index >= s.intake && index < intakeCount(a); };

export function useSeenVersion() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => ver);
}
